import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'

/**
 * Kitchen verification badge (Chef Verification & Hygiene Standards, section 4).
 *
 * Hygiene is a visible ladder, not pass/fail, so each tier has its own mark
 * and colour and the three read as steps of one family: the same shield, with
 * a check, then a drop, then a crown. The badge renders on the chef profile
 * and above the fold on every dish page.
 *
 * The tier is always the server's derived `badge_tier`. The client never
 * computes a tier: it cannot see certificate approval, flags or ratings.
 */
export type KitchenBadgeTier = 'verified' | 'hygiene_certified' | 'top_kitchen'

export const isKitchenBadgeTier = (value: unknown): value is KitchenBadgeTier =>
  value === 'verified' || value === 'hygiene_certified' || value === 'top_kitchen'

export const KITCHEN_BADGE_COPY: Record<KitchenBadgeTier, { label: string; description: string }> = {
  verified: {
    label: 'Verified',
    description: 'Identity checked, kitchen inspected by photo and video, and hygiene standards signed.',
  },
  hygiene_certified: {
    label: 'Hygiene Certified',
    description: 'Everything in Verified, plus a valid food-handler certificate on file.',
  },
  top_kitchen: {
    label: 'Top Kitchen',
    description:
      'Hygiene Certified for 6+ months with a clean record, high food-quality ratings and no open hygiene reports.',
  },
}

/* Colours are fixed per tier rather than read from the brand token: the badge
   must mean the same thing in every edition and theme. Each pairs with white
   at ≥ 4.5:1. */
const TIER_COLOR: Record<KitchenBadgeTier, string> = {
  verified: '#0B7A5A',
  hygiene_certified: '#0B7A5A',
  top_kitchen: '#B45309',
}

const Shield = ({ tier, size }: { tier: KitchenBadgeTier; size: number }) => {
  const color = TIER_COLOR[tier]
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 2.2 4.2 5.1v6.1c0 4.9 3.3 8.9 7.8 10.6 4.5-1.7 7.8-5.7 7.8-10.6V5.1L12 2.2Z"
        fill={color}
      />
      {tier === 'verified' ? (
        <path d="m8.3 12.1 2.5 2.5 4.9-5.1" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : null}
      {tier === 'hygiene_certified' ? (
        <>
          <path d="M12 6.6c1.9 2.4 3.3 4.2 3.3 6a3.3 3.3 0 0 1-6.6 0c0-1.8 1.4-3.6 3.3-6Z" fill="#fff" />
          <path d="m10.5 12.9 1.1 1.1 2-2.1" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : null}
      {tier === 'top_kitchen' ? (
        <>
          <path d="m7.6 9.2 2.3 1.9L12 7.7l2.1 3.4 2.3-1.9-.9 5.3H8.5l-.9-5.3Z" fill="#fff" />
          <rect x="8.5" y="15.4" width="7" height="1.6" rx=".8" fill="#fff" />
        </>
      ) : null}
    </svg>
  )
}

const Pill = styled.span<{ $color: string; $compact: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: ${({ $compact }) => ($compact ? '0' : '2px 7px')};
  border-radius: ${minimalVars.radius.pill};
  background: ${({ $compact, $color }) => ($compact ? 'transparent' : $color === '#B45309' ? 'rgba(180, 83, 9, 0.08)' : 'rgba(11, 122, 90, 0.08)')};
  color: ${({ $color }) => $color};
  border: ${({ $compact, $color }) => ($compact ? 'none' : $color === '#B45309' ? '1px solid rgba(180, 83, 9, 0.22)' : '1px solid rgba(11, 122, 90, 0.22)')};
  font-family: ${minimalVars.typography.bodyFamily};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: nowrap;
  vertical-align: middle;
  align-self: center;
  justify-self: start;

  & > svg {
    flex-shrink: 0;
  }
`

export interface KitchenBadgeProps {
  /** The server-derived badge_tier. `none` or unknown renders nothing. */
  tier: unknown
  /** Icon only (dense rows); the accessible name still carries the label. */
  compact?: boolean
  size?: number
}

export const KitchenBadge = ({ tier, compact = false, size }: KitchenBadgeProps) => {
  if (!isKitchenBadgeTier(tier)) return null
  const copy = KITCHEN_BADGE_COPY[tier]
  const iconSize = size ?? (compact ? 16 : 18)
  return (
    <Pill
      $color={TIER_COLOR[tier]}
      $compact={compact}
      role="img"
      aria-label={`${copy.label} kitchen: ${copy.description}`}
      title={copy.description}
      data-tier={tier}
    >
      <Shield tier={tier} size={iconSize} />
      {compact ? null : copy.label}
    </Pill>
  )
}
